
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import * as d3 from 'd3';

const animationDuration = 2000;
const animationDelay = 500;
const dashboardDarkGreen = '#81B84C';
@Component
export default class AnswerTrend extends Vue {
    @Prop() target: number;

    @Prop() value: number;

    private width: number = 220;
    private height: number = 200;
    private svgElement: any = undefined;
    private valueElement: any = undefined;

    private translateArcs = ``;

    mounted() {
        this.svgElement = d3.select(this.$el).select('svg');
        // Get width and height from svg element in case scaling is used.
        const svgBoundingClientRect = this.svgElement.node().getBoundingClientRect();
        this.translateArcs = `translate(${this.width / 2}, 120)`;
        this.refreshSpeedometer();
    }

    private scale(value: number) {
        return d3
            .scaleLinear()
            .range([-Math.PI * 0.65, Math.PI * 0.65] as ReadonlyArray<number>)
            .domain([0, 100])(value);
    }

    private drawArc(innerRadius: number, width: number, start: number, end: number, c: string, svg: any) {
        const arc = d3
            .arc()
            .innerRadius(innerRadius)
            .outerRadius(innerRadius + width)
            .startAngle(this.scale(start))
            .endAngle(this.scale(end));

        const arcElement = svg
            .append('path')
            .attr('d', arc)
            .attr('class', c)
            .attr('transform', this.translateArcs);
        return { arc, arcElement };
    }

    private drawAnimatedArc(innerRadius: number, width: number, start: number, end: number, c: string, svg: any) {
        const arc = d3
            .arc()
            .innerRadius(innerRadius)
            .outerRadius(innerRadius + width)
            .startAngle(this.scale(0))
            .endAngle(this.scale(0));

        const arcElement = svg
            .append('path')
            .attr('d', arc)
            .attr('class', c)
            .attr('transform', this.translateArcs);

        const transition = arcElement
            .transition()
            .duration(animationDuration)
            .delay(animationDelay)
            .attrTween('d', () => {
                const iStart = d3.interpolate(0, start);
                const iEnd = d3.interpolate(0, end);
                return (t: number) => {
                    arc.startAngle(this.scale(iStart(t)));
                    arc.endAngle(this.scale(iEnd(t)));
                    return arc(undefined);
                };
            });
        if (this.value >= this.target) {
            transition.style('stroke', dashboardDarkGreen);
        }
    }

    private refreshSpeedometer() {
        this.svgElement.selectAll('*').remove();

        const svg = this.svgElement.append('g');
        const minRadius = 80;

        // Speedometer background
        this.drawArc(minRadius, 0, 0, 100, 'speedometer-border', svg);
        this.drawArc(minRadius, 30, this.target, 100, 'speedometer-target-arc', svg);
        for (let i = 10; i < 100; i += 10) {
            this.drawArc(minRadius + 5, 20, i, i, 'speedometer-tick', svg);
        }

        // Speedometer value
        this.drawAnimatedArc(minRadius, 30, this.value, this.value, 'speedometer-value-arc', svg);
        this.drawAnimatedArc(minRadius, 0, 0, this.value, 'speedometer-left-border', svg);

        this.refreshInnerValue();
    }

    private refreshInnerValue() {
        const innerValue = this.svgElement.append('g').attr('class', 'speedometer-inner-value');
        const description = innerValue
            .append('text')
            .attr('width', '100%')
            .attr('class', 'speedometer-inner-value__description')
            .text('happiness %')
            .attr('transform', `translate(${this.width / 2}, 100)`);

        this.valueElement = innerValue.append('text').attr('class', 'speedometer-inner-value__number');
        const transition = this.valueElement
            .text(0)
            .attr('transform', `translate(${this.width / 2}, 165)`)
            .transition()
            .duration(animationDuration)
            .delay(animationDelay)
            .tween('text', () => {
                const i = d3.interpolate(0, this.value);
                return (t: number) => this.valueElement.text(Math.round(i(t)));
            });
        if (this.value >= this.target) {
            transition.style('fill', dashboardDarkGreen);
        }
    }
}
